/**
 * Two owner-trust banners for dunning. "Enabled" in the settings is only a
 * stored preference: the reminder service also needs a working email sender,
 * and the API reports that as `sweeper_active`. The second banner catches the
 * opposite case: reminders switched off while invoices are overdue.
 */
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Link } from 'react-router';
import { BellOff, MailWarning } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useDunningSettings } from '@/hooks/useDunningSettings';
import { useOverdueInvoiceCount } from '@/hooks/useDashboard';
import { useCanOpen } from '@/hooks/useCanOpen';

const SWEEPER_REASON: Record<string, MessageDescriptor> = {
  email_not_configured: msg({
    id: 'dunning.sweeper.reason.emailNotConfigured',
    message: 'The email sender is not set up, so no reminder can leave. Ask your Daycare Butler support to configure it.',
  }),
  startup_failed: msg({
    id: 'dunning.sweeper.reason.startupFailed',
    message: 'The reminder service did not start. Ask your Daycare Butler support to check it.',
  }),
  crashed: msg({
    id: 'dunning.sweeper.reason.crashed',
    message: 'The reminder service stopped after an error. Ask your Daycare Butler support to restart it.',
  }),
  stopped: msg({
    id: 'dunning.sweeper.reason.stopped',
    message: 'The reminder service is stopped. Ask your Daycare Butler support to start it.',
  }),
  not_started: msg({
    id: 'dunning.sweeper.reason.notStarted',
    message: 'The reminder service has not started yet. If this stays, ask your Daycare Butler support.',
  }),
  unknown: msg({
    id: 'dunning.sweeper.reason.unknown',
    message: 'The app cannot tell whether reminders are being sent. Ask your Daycare Butler support to check it.',
  }),
};

/**
 * Shown when the API says no reminder can be sent (`sweeper_active: false`), with the reason in words.
 * Left out while dunning is switched off: the off banner already says why nothing is sent.
 */
export function DunningSweeperBanner({ className }: { className?: string }) {
  const { i18n } = useLingui();
  const { data } = useDunningSettings();
  if (!data || data.sweeper_active !== false || data.enabled === false) return null;
  const given = data.sweeper_inactive_reason ?? '';
  // Own keys only: `in` would accept inherited names such as `toString`.
  // (`Object.hasOwn` needs lib ES2022; the app targets ES2020.)
  const reasonKey = Object.prototype.hasOwnProperty.call(SWEEPER_REASON, given) ? given : 'unknown';
  // "unknown" means the app cannot tell, not that reminders are off (dunning.md).
  const isUnknown = reasonKey === 'unknown';
  const reason = SWEEPER_REASON[reasonKey];
  return (
    <Alert variant="warning" className={className} data-testid="dunning-sweeper-banner">
      <MailWarning className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>
        {isUnknown
          ? i18n._({ id: 'dunning.sweeper.titleUnknown', message: 'The app cannot confirm that reminders are sent' })
          : i18n._({ id: 'dunning.sweeper.title', message: 'Payment reminders are not being sent' })}
      </AlertTitle>
      <AlertDescription>{i18n._(reason)}</AlertDescription>
    </Alert>
  );
}

/**
 * Shown when dunning is switched off while invoices are overdue. The link to
 * the settings is left out on the settings page itself.
 */
export function DunningOffBanner({
  showSettingsLink = true,
  className,
}: {
  showSettingsLink?: boolean;
  className?: string;
}) {
  const { i18n } = useLingui();
  const canOpen = useCanOpen();
  const { data: settings } = useDunningSettings();
  const isOff = settings?.enabled === false;
  const overdue = useOverdueInvoiceCount({ enabled: isOff });
  const count = overdue.isSuccess ? (overdue.data ?? 0) : 0;
  if (!isOff || count === 0) return null;
  return (
    <Alert variant="warning" className={className} data-testid="dunning-off-banner">
      <BellOff className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>
        {i18n._({
          id: 'dunning.off.title',
          message: '{count, plural, one {# invoice is overdue} other {# invoices are overdue}} and reminders are off',
          values: { count },
        })}
      </AlertTitle>
      <AlertDescription>
        {i18n._({
          id: 'dunning.off.description',
          message: 'No family gets a payment reminder until dunning is switched on.',
        })}{' '}
        {showSettingsLink && canOpen('/settings/dunning') && (
          <Link to="/settings/dunning" className="font-medium underline hover:no-underline">
            {i18n._({ id: 'dunning.off.action', message: 'Switch on reminders' })}
          </Link>
        )}
      </AlertDescription>
    </Alert>
  );
}

/** Both banners, for the dunning cockpit. */
export function DunningStatusBanners() {
  return (
    <>
      <DunningSweeperBanner />
      <DunningOffBanner />
    </>
  );
}
